import React, {useState, useEffect, useContext} from 'react';
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import {styled} from "@mui/material/styles";
import {Icon, Rating, Snackbar} from "@mui/material";
import Button from "@mui/material/Button";
import {Box} from "@mui/material";
import Typography from "@mui/material/Typography";
import AppDrawer from "../AppDrawer/AppDrawer";
import ConstructionIcon from '@mui/icons-material/Construction';
import TextField from "@mui/material/TextField";
import {UserContext} from "../../context/userContext";
import {green} from "@mui/material/colors";
import CircularProgress from "@mui/material/CircularProgress";
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import {AuthContext} from "../../context/authContext";
import frugalServicesClient from "../../utils/frugal-services-client";
import Alert from '@mui/material/Alert';

const PREFIX = 'DashboardDashboard';
const classes = {
   root: `${PREFIX}-root`,
   paper: `${PREFIX}-paper`,
   aboutContainer: `${PREFIX}-aboutContainer`,
   image: `${PREFIX}-image`,
   ratingContainer: `${PREFIX}-ratingContainer`,
   feedbackContainer: `${PREFIX}-feedbackContainer`
}
const Root = styled('div')(({ theme }) => ({
   [`&.${classes.root}`]: {
      display: "flex",
      paddingTop: '64px'
   },
   [`& .${classes.paper}`]: {
      padding: theme.spacing(2),
      color: theme.palette.text.secondary,
      width: '100%'
   },
   [`& .${classes.aboutContainer}`]: {
      padding: theme.spacing(2),
   },
   [`& .${classes.image}`]: {
      maxWidth: '100%',
      height: 'auto',
      margin: theme.spacing(2, 0),
   },
   [`& .${classes.ratingContainer}`]: {
      margin: theme.spacing(2, 0),
   },
   [`& .${classes.feedbackContainer}`]: {
      padding: '25px'
   },
}))


export default function Contact(props) {
   const {setPasswordExpired} = useContext(AuthContext);
   const {user, userLoading} = useContext(UserContext);
   const [rating, setRating] = useState(0);
   const [feedback, setFeedback] = useState("");
   const [loading, setLoading] = React.useState(false);
   const [success, setSuccess] = React.useState(false);
   const [openSnackbar, setOpenSnackbar] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState("");
   const [snackbarSeverity, setSnackbarSeverity] = useState("success");

   const steps = ['$0: No Revenue', ' $100: Hosting is paid',];

   const buttonSx = {
      ...(success && {
         bgcolor: green[500],
         '&:hover': {
            bgcolor: green[700],
         },
      }),
   };


   const handleRatingChange = (event, value) => {
      setRating(value);
   };
   const handleFeedbackChange = e => {
      setFeedback(e.target.value);
   }

   const handleCloseSnackbar = (event, reason) => {
      if (reason === 'clickaway') {
         return;
      }
      setOpenSnackbar(false);
   };


   // Handle feedback submission
   const handleSendFeedback = () => {
      if (loading) return;

      setLoading(true);
      setSuccess(false);

      frugalServicesClient.sendFeedback({
         userId: user.userId,
         accountId: user.accountId,
         rating: rating,
         feedback: feedback
      }).then(function(response) {
         setLoading(false);
         // Exit and require login
         if(response.status === 401) {
            setPasswordExpired(true);
            return;
         }

         if (response.ok) {
            setSuccess(true);
            setRating(0);
            setFeedback("");
            setSnackbarMessage("Feedback sent successfully! Thank you.");
            setSnackbarSeverity("success");
            setOpenSnackbar(true);

            // Reset success state after 3 seconds to revert button color
            setTimeout(() => {
               setSuccess(false);
            }, 3000);
         } else {
            setSnackbarMessage("Failed to send feedback. Please try again.");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
         }
      }).catch(function (error) {
         setLoading(false);
         console.error('Submit feedback failure: ', error);
         setSnackbarMessage("An error occurred. Please try again.");
         setSnackbarSeverity("error");
         setOpenSnackbar(true);
      });

   };



   return (
      <Root className={classes.root}>
         <AppDrawer/>
         <Paper className={classes.paper}>
            <Grid container>
               <div className={classes.aboutContainer}>
                  <Typography variant="h4" gutterBottom>
                     About Our Budgeting App
                  </Typography>
                  <Typography paragraph>
                     <ConstructionIcon/> Constructive criticism accepted. Tell me how you really feel. If you'd like to consider supporting, please buy me a coffee.
                  </Typography>

                  <Grid>
                        <Typography variant="h6" gutterBottom>
                           Rate Our App
                        </Typography>
                        <Box component="fieldset" mb={3} borderColor="transparent">
                           <Rating
                              name="rating"
                              value={rating}
                              precision={1}
                              onChange={handleRatingChange}
                           />
                        </Box>
                  </Grid>
                  <Grid className={classes.feedbackContainer}>
                     <TextField
                        id="outlined-multiline-static"
                        label="Feedback"
                        multiline
                        rows={4}
                        value={feedback}
                        style={{width: "100%"}}
                        onChange={handleFeedbackChange}
                     />
                  </Grid>
                  <Grid>
                     <Box sx={{display: 'flex', justifyContent: 'flex-end', paddingTop: '15px'}}>
                        <Box sx={{display: 'flex', justifyContent: 'center'}}>
                           <Button variant="contained" sx={buttonSx} color="primary" disabled={loading} onClick={handleSendFeedback}>
                              {success ? "Sent!" : "Submit Feedback"}
                           </Button>
                           {loading && (
                              <CircularProgress
                                 size={24}
                                 sx={{
                                    color: green[500],
                                    position: 'relative',
                                    top: 5,
                                    left: -92,
                                    zIndex: 1,
                                 }}
                              />
                           )}
                        </Box>
                     </Box>
                  </Grid>
               </div>
            </Grid>

            <Grid container>
               <Grid size={12}>
                  <Typography variant="h4" gutterBottom>
                     Transparent Revenue
                  </Typography>
               </Grid>

               <Grid size={12}>
                  <Paper sx={{padding: "15px"}}>
                     <Stepper activeStep={0}>
                        {steps.map((label, index) => {
                           const stepProps = {};
                           const labelProps = {};
                           return (
                              <Step key={label} {...stepProps}>
                                 <StepLabel {...labelProps}>{label}</StepLabel>
                              </Step>
                           );
                        })}
                     </Stepper>
                  </Paper>
               </Grid>
            </Grid>
         </Paper>
         <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
            <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Root>
   );
}